/** Registry of transactional email templates editable in backoffice. */

export interface EmailTemplateDef {
  id: string;
  title: string;
  subjectKey: string;
  bodyKey: string;
  tokens: readonly string[];
  defaultSubject: string;
  defaultBody: string;
}

export const EMAIL_LAYOUT_KEYS = {
  footerText: "email.footer_text",
  primaryColor: "email.primary_color",
  accentColor: "email.accent_color",
} as const;

export const EMAIL_TEMPLATES = [
  {
    id: "verify_email",
    title: "Verify email",
    subjectKey: "email.verify_email.subject",
    bodyKey: "email.verify_email.body",
    tokens: ["verifyUrl"],
    defaultSubject: "Verify your Mr Gaole account",
    defaultBody: `Welcome to Mr Gaole! Confirm your email to start collecting.

[Verify email]({{verifyUrl}})

Or copy this link: {{verifyUrl}}`,
  },
  {
    id: "password_reset",
    title: "Password reset",
    subjectKey: "email.password_reset.subject",
    bodyKey: "email.password_reset.body",
    tokens: ["resetUrl"],
    defaultSubject: "Reset your Mr Gaole password",
    defaultBody: `We received a request to reset your password.

[Reset password]({{resetUrl}})

If you didn't request this, you can ignore this email.`,
  },
  {
    id: "mfa_code",
    title: "Sign-in code",
    subjectKey: "email.mfa_code.subject",
    bodyKey: "email.mfa_code.body",
    tokens: ["code"],
    defaultSubject: "Your Mr Gaole sign-in code",
    defaultBody: `Your verification code is:

## {{code}}

This code expires in 10 minutes. If you didn't try to sign in, you can ignore this email.`,
  },
  {
    id: "order_confirmed",
    title: "Order confirmed",
    subjectKey: "email.order_confirmed.subject",
    bodyKey: "email.order_confirmed.body",
    tokens: ["orderNo", "total", "currency"],
    defaultSubject: "Order {{orderNo}} confirmed — Mr Gaole",
    defaultBody: `Thanks for your order! We've received your payment of **{{total}} {{currency}}**.

Order number: **{{orderNo}}**

You can track your order anytime from your account.`,
  },
  {
    id: "order_shipped",
    title: "Order shipped",
    subjectKey: "email.order_shipped.subject",
    bodyKey: "email.order_shipped.body",
    tokens: ["orderNo", "trackingNo", "courier"],
    defaultSubject: "Your Mr Gaole order {{orderNo}} is on the way",
    defaultBody: `Your order has been shipped!

Tracking number: **{{trackingNo}}**{{courier}}

Track it anytime at [EasyTrack](https://easyparcel.com/my/easytrack/) or on your Mr Gaole account.`,
  },
  {
    id: "flexxi_kyc_approved",
    title: "FlexxiPlan identity approved",
    subjectKey: "email.flexxi_kyc_approved.subject",
    bodyKey: "email.flexxi_kyc_approved.body",
    tokens: [],
    defaultSubject: "You're verified for FlexxiPlan — Mr Gaole",
    defaultBody: `Your identity verification was approved. You can now start FlexxiPlan instalments from the shop or marketplace.`,
  },
  {
    id: "flexxi_kyc_rejected",
    title: "FlexxiPlan identity rejected",
    subjectKey: "email.flexxi_kyc_rejected.subject",
    bodyKey: "email.flexxi_kyc_rejected.body",
    tokens: ["reviewNote"],
    defaultSubject: "FlexxiPlan verification update — Mr Gaole",
    defaultBody: `We couldn't approve your identity verification.

{{reviewNote}}

Sign in and open **Account → Flexxi verification** to submit updated documents.`,
  },
  {
    id: "auction_won",
    title: "Auction won",
    subjectKey: "email.auction_won.subject",
    bodyKey: "email.auction_won.body",
    tokens: ["title", "productName", "price", "deadlineHours"],
    defaultSubject: "You won — {{title}}",
    defaultBody: `Congratulations! You won **{{productName}}** for **RM {{price}}**.

Complete payment within **{{deadlineHours}} hours** to claim it.`,
  },
  {
    id: "raffle_won",
    title: "Raffle won",
    subjectKey: "email.raffle_won.subject",
    bodyKey: "email.raffle_won.body",
    tokens: ["title", "productName", "ticketNo", "amount", "deadlineHours"],
    defaultSubject: "You WON — {{title}}",
    defaultBody: `Congratulations! Your ticket **#{{ticketNo}}** won **{{productName}}**.

The item is reserved in your orders. Complete payment of **RM {{amount}}** within **{{deadlineHours}} hours** or the win is forfeited.`,
  },
  {
    id: "raffle_lobby_won",
    title: "Raffle lobby won",
    subjectKey: "email.raffle_lobby_won.subject",
    bodyKey: "email.raffle_lobby_won.body",
    tokens: ["raffleTitle", "productName", "ticketNo", "amount", "deadlineHours"],
    defaultSubject: "You WON — {{productName}}",
    defaultBody: `Congratulations! Your ticket **#{{ticketNo}}** won **{{productName}}** in {{raffleTitle}}.

Complete payment of **RM {{amount}}** within **{{deadlineHours}} hours** to claim your prize.`,
  },
] as const satisfies readonly EmailTemplateDef[];

export type EmailTemplateId = (typeof EMAIL_TEMPLATES)[number]["id"];

const ID_INDEX = Object.fromEntries(EMAIL_TEMPLATES.map((t) => [t.id, t])) as Record<
  EmailTemplateId,
  (typeof EMAIL_TEMPLATES)[number]
>;

export function isEmailTemplateId(id: string): id is EmailTemplateId {
  return id in ID_INDEX;
}

export function emailTemplateById(id: string): EmailTemplateDef | undefined {
  return isEmailTemplateId(id) ? ID_INDEX[id] : undefined;
}

export function emailTemplateKeys(id: EmailTemplateId): [string, string] {
  const t = ID_INDEX[id];
  return [t.subjectKey, t.bodyKey];
}

export function emailTemplateTokenHint(id: EmailTemplateId): string {
  return ID_INDEX[id].tokens.map((t) => `{{${t}}}`).join(", ");
}

/** All setting keys for email templates + layout (for bulk save/seed). */
export function allEmailSettingKeys(): string[] {
  const templateKeys = EMAIL_TEMPLATES.flatMap((t) => [t.subjectKey, t.bodyKey]);
  return [...templateKeys, EMAIL_LAYOUT_KEYS.footerText, EMAIL_LAYOUT_KEYS.primaryColor, EMAIL_LAYOUT_KEYS.accentColor];
}
